import { parseISO } from 'date-fns';

import { Country } from 'common/src/constants/countries';
import { healZipPostalCodeValue } from 'common/src/transformers/healZipPostalCodeValue';
import { AssetType } from 'common/src/types/enums/EnergyAssetType.enum';
import { EnergyFlowType } from 'common/src/types/enums/EnergyFlowType.enum';
import { EnergyType } from 'common/src/types/enums/EnergyType.enum';
import { OperationalStatus } from 'common/src/types/enums/OperationalStatus.enum';
import { OwnershipType } from 'common/src/types/enums/OwnershipType.enum';
import { SiteLocation } from 'common/src/types/enums/SiteLocation.enum';
import { IEnergyAsset, IFacility } from 'common/src/types/interfaces';

import { getLocationFormFields } from 'portal/src/components/common/forms/locationFormFields';

import { getAddressFields } from '../../../common/forms/commonFormFields';
import {
  autoCompleteType,
  datePickerType,
  noFieldType,
  numberType,
  patternInputType,
  selectInputType,
  textInputType,
} from '../../../common/forms/FormFieldTypes';

export const defaultValues = (entity: Partial<IEnergyAsset> = {}) => ({
  address1: entity.address1 ?? '',
  address2: entity.address2 ?? '',
  assetDescription: entity.assetDescription ?? '',
  assetType: entity.assetType ?? '',
  city: entity.city ?? '',
  chargingPorts: entity.chargingPorts ?? null,
  country: (entity.country ?? '') as Country,
  energyFlowType: entity.energyFlowType ?? '',
  energyType: entity.energyType ?? '',
  facilityId: entity.facilityId ?? null,
  group: entity.group ?? '',
  installationDate: entity.installationDate
    ? parseISO(entity.installationDate)
    : null,
  latitude: entity.latitude ?? '',
  longitude: entity.longitude ?? '',
  macAddress: entity.macAddress ?? '',
  manufacturer: entity.manufacturer ?? '',
  model: entity.model ?? '',
  name: entity.name ?? '',
  operationalStatus: entity.operationalStatus ?? null,
  ownershipType: entity.ownershipType ?? null,
  postalCode: healZipPostalCodeValue(entity.postalCode, entity.country) ?? '',
  serialNumber: entity.serialNumber ?? '',
  siteLocation: entity.siteLocation ?? '',
  state: entity.state ?? '',
  timezone: entity.timezone ?? '',
  vendorId: entity.vendorId ?? '',
  vendorName: entity.vendorName ?? '',
});

export const getAssetInformationFields = () => {
  const formFieldsConfiguration: Array<
    | autoCompleteType
    | datePickerType
    | numberType
    | selectInputType
    | textInputType
  > = [
    {
      placeholder: 'Name',
      label: 'Name',
      name: 'name',
      type: 'textInput',
      required: true,
    },
    {
      placeholder: 'Site Location',
      label: 'Site Location',
      name: 'siteLocation',
      type: 'selectInput',
      required: true,
      values: Object.values(SiteLocation).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Asset Type',
      label: 'Asset Type',
      name: 'assetType',
      type: 'selectInput',
      required: true,
      values: Object.values(AssetType).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Energy Type',
      label: 'Energy Type',
      name: 'energyType',
      type: 'selectInput',
      required: true,
      values: Object.values(EnergyType).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Energy Flow Type',
      label: 'Energy Flow Type',
      name: 'energyFlowType',
      type: 'selectInput',
      required: true,
      values: Object.values(EnergyFlowType).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Manufacturer',
      label: 'Manufacturer',
      name: 'manufacturer',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Model',
      label: 'Model',
      name: 'model',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Serial Number',
      label: 'Serial Number',
      name: 'serialNumber',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'MAC Address',
      label: 'MAC Address',
      name: 'macAddress',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Charging Ports',
      label: 'Charging Ports',
      name: 'chargingPorts',
      type: 'number',
      required: false,
    },
    {
      placeholder: 'Asset Description',
      label: 'Asset Description',
      multiline: true,
      name: 'assetDescription',
      type: 'textInput',
      required: false,
    },
  ];
  return formFieldsConfiguration;
};

export const getLocationDetailsFields = (
  facilities: Partial<IFacility>[] = [],
  country: Country,
  onFacilityIdChange?: (value: string | null) => void,
) => {
  const formFieldsConfiguration: Array<
    | autoCompleteType
    | datePickerType
    | noFieldType
    | patternInputType
    | selectInputType
    | textInputType
  > = [
    {
      placeholder: 'Facility',
      label: 'Facility',
      name: 'facilityId',
      type: 'autocomplete',
      required: false,
      onChange: onFacilityIdChange,
      options: facilities.map((item) => ({
        label: item.name,
        value: item.id,
        key: item.id,
        disabled: !item.isActive,
      })),
      prependDivider: 'Location Details',
    },
    {
      type: 'noField',
      name: 'noField',
    },
    ...getAddressFields(false, false, country),
    ...getLocationFormFields(),
    {
      placeholder: 'Group',
      label: 'Group',
      name: 'group',
      type: 'textInput',
      required: false,
    },
  ];

  return formFieldsConfiguration;
};

export const getOperationalInformationFields = () => {
  const formFieldsConfiguration: Array<
    autoCompleteType | datePickerType | selectInputType | textInputType
  > = [
    {
      placeholder: 'Installation Date',
      label: 'Installation Date',
      name: 'installationDate',
      type: 'datePicker',
      required: false,
      prependDivider: 'Operational Information',
    },
    {
      placeholder: 'Operational Status',
      label: 'Operational Status',
      name: 'operationalStatus',
      type: 'selectInput',
      required: false,
      values: Object.values(OperationalStatus).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Ownership Type',
      label: 'Ownership Type',
      name: 'ownershipType',
      type: 'selectInput',
      required: false,
      values: Object.values(OwnershipType).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      placeholder: 'Vendor Name',
      label: 'Vendor Name',
      name: 'vendorName',
      type: 'textInput',
      required: false,
    },
    {
      placeholder: 'Vendor ID',
      label: 'Vendor ID',
      name: 'vendorId',
      type: 'textInput',
      required: false,
      disabled: true,
    },
  ];
  return formFieldsConfiguration;
};
